import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Shield, Award, Users, Stethoscope } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const MARATHI_DIGITS = ['०', '१', '२', '३', '४', '५', '६', '७', '८', '९'];
const KANNADA_DIGITS = ['೦', '೧', '೨', '೩', '೪', '೫', '೬', '೭', '೮', '೯'];

const formatLocalizedNumber = (val, language, useComma) => {
  const formatted = useComma ? val.toLocaleString('en-IN') : String(val);
  if (language === 'mr') {
    return formatted.replace(/[0-9]/g, (digit) => MARATHI_DIGITS[Number(digit)]);
  }
  if (language === 'kn') {
    return formatted.replace(/[0-9]/g, (digit) => KANNADA_DIGITS[Number(digit)]);
  }
  return formatted;
};

// Running counter component that smoothly animates from 0 to target
const RunningCounter = ({ target, isRunning, duration = 2000, suffix = '', useComma = false, language }) => {
  const [currentVal, setCurrentVal] = useState(0);

  useEffect(() => {
    if (!isRunning) {
      setCurrentVal(0);
      return;
    }

    let startTime = null;
    let animationFrameId;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Cubic ease-out: fast start, soft and gentle deceleration towards finish
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const calculated = Math.floor(easeOut * target);

      setCurrentVal(calculated);

      if (progress < 1) {
        animationFrameId = requestAnimationFrame(animate);
      } else {
        setCurrentVal(target);
      }
    };

    animationFrameId = requestAnimationFrame(animate);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [isRunning, target, duration]);

  return (
    <span className="tabular-nums inline-flex items-baseline font-black tracking-tight">
      <span>{formatLocalizedNumber(currentVal, language, useComma)}</span>
      {suffix && (
        <span className="text-teal font-extrabold ml-0.5 text-2xl sm:text-3xl lg:text-4xl">
          {suffix}
        </span>
      )}
    </span>
  );
};

const TrustSection = () => {
  const { t, language } = useLanguage();
  const sectionRef = useRef(null);
  const [hasStarted, setHasStarted] = useState(false);

  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;

    if (!('IntersectionObserver' in window)) {
      setHasStarted(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasStarted(true);
          observer.disconnect();
        }
      },
      { threshold: 0.25 }
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, []);

  const stats = [
    { 
      icon: Users, 
      target: 50000, 
      suffix: '+', 
      useComma: true, 
      label: t('trust.happyPatients'),
      glow: 'group-hover:border-teal/50 group-hover:shadow-teal/20',
      iconBg: 'bg-teal/15 text-teal border-teal/30',
    },
    { 
      icon: Stethoscope, 
      target: 20, 
      suffix: '+', 
      useComma: false, 
      label: t('trust.expertDoctors'),
      glow: 'group-hover:border-clinical/50 group-hover:shadow-clinical/20',
      iconBg: 'bg-clinical/15 text-clinical border-clinical/30',
    },
    { 
      icon: Shield, 
      target: 25, 
      suffix: '+', 
      useComma: false, 
      label: t('trust.yearsExperience'),
      glow: 'group-hover:border-accent/50 group-hover:shadow-accent/20',
      iconBg: 'bg-accent/15 text-accent border-accent/30',
    },
    { 
      icon: Award, 
      target: 6, 
      suffix: '', 
      useComma: false, 
      label: t('trust.advancedBranches'),
      glow: 'group-hover:border-emerald-400/50 group-hover:shadow-emerald-400/20',
      iconBg: 'bg-emerald-400/15 text-emerald-400 border-emerald-400/30',
    },
  ];

  return (
    <section 
      ref={sectionRef} 
      className="py-16 md:py-20 bg-gradient-to-b from-[#0F2338] via-navy to-[#0F2338] text-white relative overflow-hidden"
    >
      {/* Background ambient lighting effects */}
      <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-96 h-96 bg-teal/10 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute top-1/2 right-1/4 -translate-y-1/2 w-96 h-96 bg-clinical/10 rounded-full blur-3xl pointer-events-none"></div>

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 25 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`group relative p-5 sm:p-7 rounded-2xl bg-white/[0.05] hover:bg-white/[0.08] border border-white/10 ${stat.glow} transition-all duration-300 flex flex-col items-center text-center backdrop-blur-sm shadow-xl hover:-translate-y-1`}
              >
                {/* Icon with glowing badge */}
                <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-2xl border ${stat.iconBg} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-md`}>
                  <Icon size={30} strokeWidth={1.8} />
                </div>

                {/* Animated Running Counter */}
                <div className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-2 leading-none">
                  <RunningCounter
                    target={stat.target}
                    isRunning={hasStarted}
                    duration={2000}
                    suffix={stat.suffix}
                    useComma={stat.useComma}
                    language={language}
                  />
                </div>

                {/* Stat Label */}
                <p className="text-gray-300 text-xs sm:text-sm lg:text-base font-medium leading-snug line-clamp-2">
                  {stat.label}
                </p>

                {/* Subtle bottom indicator line on hover */}
                <div className="w-8 h-[2px] bg-white/10 group-hover:w-16 group-hover:bg-teal transition-all duration-300 mt-4 rounded-full"></div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default TrustSection;
